import { IAudienceForm, AUDIENCE_CREATION_METHOD, AUDIENCE_CSV_IMPORT_TYPE } from '@/types/audiences';
import { v4 as uuidv4 } from 'uuid';

// Re-export enums for backward compatibility (enums moved to @/types/audiences to break circular dependency)
export { AUDIENCE_CREATION_METHOD, AUDIENCE_CSV_IMPORT_TYPE };

export enum AUDIENCE_PERMISSION {
  ANYONE = 'user',
  ONLY_ADMIN = 'admin',
}

export const OPERATOR_LABEL = {
  // CamelCase operators (used by facts)
  equal: 'Equal (==)',
  notEqual: 'Not Equal (!=)',
  equals: 'Equals (==)', // Alias for equal
  notEquals: 'Not Equals (!=)', // Alias for notEqual
  contains: 'Contains',
  startsWith: 'Starts With',
  endsWith: 'Ends With',
  greaterThan: 'Greater Than (>)',
  lessThan: 'Less Than (<)',
  greaterThanInclusive: 'Greater Than or Equal (>=)',
  lessThanInclusive: 'Less Than or Equal (<=)',
  in: 'In (Array Contains)',
  notIn: 'Not In (Array Does Not Contain)',
  notNull: 'Not Null',
  // Snake_case operators (used by CRM filters and some backend APIs)
  not_equals: 'Not Equal (!=)',
  greater_than: 'Greater Than (>)',
  less_than: 'Less Than (<)',
  greater_than_or_equal: 'Greater Than or Equal (>=)',
  less_than_or_equal: 'Less Than or Equal (<=)',
  not_contains: 'Does Not Contain',
  starts_with: 'Starts With',
  ends_with: 'Ends With',
  not_in: 'Not In',
  has_property: 'Is Known',
  not_has_property: 'Is Unknown',
};

export const AUDIENCES_PERMISSION_OPTIONS = [
  {
    value: AUDIENCE_PERMISSION.ANYONE,
    label: 'Anyone can edit',
  },
  {
    value: AUDIENCE_PERMISSION.ONLY_ADMIN,
    label: 'Only admins can edit',
  },
];

// AUDIENCE_CSV_IMPORT_TYPE moved to @/types/audiences to break circular dependency

export const CSV_IMPORT_TYPE_OPTIONS = [
  {
    value: AUDIENCE_CSV_IMPORT_TYPE.PERSON,
    label: 'Person',
  },
  {
    value: AUDIENCE_CSV_IMPORT_TYPE.COMPANY,
    label: 'Company',
  },
];

export const AUDIENCE_CSV_TEMPLATE = {
  [AUDIENCE_CSV_IMPORT_TYPE.PERSON]: {
    columns: [
      {
        name: 'First Name',
        key: 'first_name',
        required: true,
        description: 'The first name of the individual',
        suggested_mappings: ['First', 'Given Name'],
      },
      {
        name: 'Last Name',
        key: 'last_name',
        required: true,
        description: 'The last name of the individual',
        suggested_mappings: ['Last', 'Surname'],
      },
      {
        name: 'Email',
        key: 'email',
        required: true,
        description: "The individual's email address",
        data_type: 'email',
        suggested_mappings: ['Email Address', 'Contact Email'],
      },
      {
        name: 'Company Name',
        key: 'company_name',
        required: false,
        description: 'The name of the company the individual is associated with',
        suggested_mappings: ['Company', 'Organization'],
      },
      {
        name: 'Phone',
        key: 'phone',
        required: false,
        description: "The individual's contact number",
        data_type: 'string',
        suggested_mappings: ['Contact Number', 'Phone Number'],
      },
      {
        name: 'Job Title',
        key: 'job_title',
        required: false,
        description: "The individual's job title or role",
        suggested_mappings: ['Title', 'Role'],
      },
      {
        name: 'Website',
        key: 'website',
        required: false,
        description: "The individual's or their company's website URL",
        data_type: 'string',
        suggested_mappings: ['Website URL', 'Site', 'Company Website'],
      },
      {
        name: 'LinkedIn Profile URL',
        key: 'linkedin_url',
        required: false,
        description: "The individual's LinkedIn profile URL",
        data_type: 'string',
        suggested_mappings: ['LinkedIn', 'LinkedIn Profile'],
      },
      {
        name: 'HubSpot Record ID',
        key: 'hs_record_id',
        required: false,
        description: 'Internal HubSpot record identifier',
        suggested_mappings: ['HubSpot ID', 'HS ID'],
      },
      {
        name: 'Salesforce Record ID',
        key: 'sf_record_id',
        required: false,
        description: 'Internal Salesforce record identifier',
        suggested_mappings: ['SFDC ID', 'Salesforce ID'],
      },
      {
        name: 'Industry',
        key: 'industry',
        required: false,
        description: 'Industry / vertical',
      },
      {
        name: 'Dynamic Column 1',
        key: 'dynamic_col_1',
        required: false,
        description: 'Custom data – will be available downstream as dynamic_col_1',
      },
      {
        name: 'Dynamic Column 2',
        key: 'dynamic_col_2',
        required: false,
        description: 'Custom data – will be available downstream as dynamic_col_2',
      },
      {
        name: 'Dynamic Column 3',
        key: 'dynamic_col_3',
        required: false,
        description: 'Custom data – will be available downstream as dynamic_col_3',
      },
    ],
  },

  [AUDIENCE_CSV_IMPORT_TYPE.COMPANY]: {
    columns: [
      {
        name: 'Company Name',
        key: 'company_name',
        required: false,
        description: 'The name of the company',
        suggested_mappings: ['Company', 'Business Name'],
      },
      {
        name: 'Phone',
        key: 'phone',
        required: false,
        description: 'The primary contact number for the company',
        data_type: 'string',
        suggested_mappings: ['Contact Number', 'Phone Number'],
      },
      {
        name: 'Website',
        key: 'website',
        required: false,
        description: "The company's website URL",
        data_type: 'string',
        suggested_mappings: ['Website URL', 'Site'],
      },
      {
        name: 'Address',
        key: 'address',
        required: false,
        description: 'The physical address of the company',
        suggested_mappings: ['Address', 'Street Address'],
      },
      {
        name: 'City',
        key: 'city',
        required: false,
        description: 'The city where the company is located',
      },
      {
        name: 'State',
        key: 'state',
        required: false,
        description: "The state or province of the company's location",
        suggested_mappings: ['State', 'Province'],
      },
      {
        name: 'Country',
        key: 'country',
        required: false,
        description: 'The country the company is in — Find contacts searches for people in this country only',
        suggested_mappings: ['Country', 'Country Name', 'Region', 'Market', 'Location'],
      },
      {
        name: 'Contact First Name',
        key: 'contact_first_name',
        required: false,
        description: "First name of the company's primary contact",
        suggested_mappings: ['First Name', 'Contact First'],
      },
      {
        name: 'Contact Last Name',
        key: 'contact_last_name',
        required: false,
        description: "Last name of the company's primary contact",
        suggested_mappings: ['Last Name', 'Contact Last'],
      },
      {
        name: 'Email',
        key: 'email',
        required: false,
        description: "Email of the company's primary contact",
        data_type: 'email',
      },
      {
        name: 'Job Title',
        key: 'job_title',
        required: false,
        description: 'The job title or role at the company',
        suggested_mappings: ['Title', 'Role', 'Position'],
      },
      {
        name: 'Company LinkedIn Page URL',
        key: 'linkedin_url',
        required: false,
        description: "The company's LinkedIn page URL",
        data_type: 'string',
        suggested_mappings: ['Company LinkedIn', 'LinkedIn Page'],
      },
      {
        name: 'HubSpot Record ID',
        key: 'hs_record_id',
        required: false,
        description: 'Internal HubSpot record identifier',
        suggested_mappings: ['HubSpot ID', 'HS ID'],
      },
      {
        name: 'Salesforce Record ID',
        key: 'sf_record_id',
        required: false,
        description: 'Internal Salesforce record identifier',
        suggested_mappings: ['SFDC ID', 'Salesforce ID'],
      },
      {
        name: 'Industry',
        key: 'industry',
        required: false,
        description: 'Industry / vertical',
      },
      {
        name: 'Dynamic Column 1',
        key: 'dynamic_col_1',
        required: false,
        description: 'Custom data – will be available downstream as dynamic_col_1',
      },
      {
        name: 'Dynamic Column 2',
        key: 'dynamic_col_2',
        required: false,
        description: 'Custom data – will be available downstream as dynamic_col_2',
      },
      {
        name: 'Dynamic Column 3',
        key: 'dynamic_col_3',
        required: false,
        description: 'Custom data – will be available downstream as dynamic_col_3',
      },
    ],
  },
};

// AUDIENCE_CREATION_METHOD moved to @/types/audiences to break circular dependency

export const AUDIENCES_CREATION_METHOD_OPTIONS = [
  {
    label: 'Website Visits',
    value: AUDIENCE_CREATION_METHOD.RULES,
    description: 'Build segments based on website visitor behavior',
  },
  {
    label: 'Upload File',
    value: AUDIENCE_CREATION_METHOD.CSV,
    description: 'Import audience from CSV or Excel files',
  },
  {
    label: 'HubSpot Contacts',
    value: AUDIENCE_CREATION_METHOD.HUBSPOT,
    description: 'Import contacts directly from your connected HubSpot account',
  },
  {
    label: 'Salesforce Contacts',
    value: AUDIENCE_CREATION_METHOD.SALESFORCE,
    description: 'Import contacts directly from your connected Salesforce account',
  },
  {
    label: 'GTM Signal',
    value: AUDIENCE_CREATION_METHOD.GTM_SIGNAL,
    description: 'Triggered workflows from GTM signals',
  },
  {
    label: 'LinkedIn Post Commenters',
    value: AUDIENCE_CREATION_METHOD.LINKEDIN_POST_COMMENTERS,
    description: 'Import commenters from a LinkedIn post',
  },
  {
    label: 'Conference Scout',
    value: AUDIENCE_CREATION_METHOD.CONFERENCE_SCOUT,
    description: 'Scout conference speakers and export results',
  },
  {
    label: 'Pre-Meeting Brief',
    value: AUDIENCE_CREATION_METHOD.PRE_MEETING_BRIEF,
    description: 'AI-generated meeting briefs delivered via Slack',
  },
  {
    label: 'AI Search',
    value: AUDIENCE_CREATION_METHOD.AUDIENCE_BUILDER,
    description: 'Audiences created via the AI-powered audience builder',
  },
  {
    label: 'Scheduled Import',
    value: AUDIENCE_CREATION_METHOD.SCHEDULED_IMPORT,
    description: 'Recurring CRM (Salesforce or HubSpot) import on a schedule',
  },
  {
    label: '6sense Accounts',
    value: AUDIENCE_CREATION_METHOD.SIXSENSE,
    description: 'Account audiences built from your 6sense intent, firmographic, and segment data',
  },
];

// Creation methods unchecked by default in the filter (system-generated audiences).
// Other system types like salesforce_cti and single_call are excluded implicitly
// because they don't appear in AUDIENCES_CREATION_METHOD_OPTIONS.
const AUDIENCES_EXCLUDED_CREATION_METHODS: readonly string[] = [
  AUDIENCE_CREATION_METHOD.GTM_SIGNAL,
  AUDIENCE_CREATION_METHOD.PRE_MEETING_BRIEF,
];

// Default creation method filter: all visible options minus excluded ones,
// plus legacy types that still exist in the database but are no longer creatable
export const DEFAULT_CREATION_METHOD_FILTER: string[] = [
  ...AUDIENCES_CREATION_METHOD_OPTIONS
    .map((o) => o.value)
    .filter((v) => !AUDIENCES_EXCLUDED_CREATION_METHODS.includes(v)),
  AUDIENCE_CREATION_METHOD.VOICE,
  AUDIENCE_CREATION_METHOD.TIME_BASED,
  AUDIENCE_CREATION_METHOD.CHROME_EXTENSION,
  // website_visits is distinct from 'rules' — set when user imports via "Visited Before" flow
  AUDIENCE_CREATION_METHOD.WEBSITE_VISITS,
  AUDIENCE_CREATION_METHOD.FIBER_TAM,
  // ai_search exists in the DB but has no creation UI — legacy type.
  // power_dialer is intentionally omitted: those rows are internal per-session
  // execution artifacts and belong in Power Dialer history, not Audiences.
  AUDIENCE_CREATION_METHOD.AI_SEARCH,
  // dialer_queue: persistent ready-to-call audiences maintained by Add to
  // Dialer workflow nodes (epic #3561) — workspace-visible by design, unlike
  // power_dialer session artifacts.
  AUDIENCE_CREATION_METHOD.DIALER_QUEUE,
  // Nurture loop audiences (mb-frontend#4458) are normal audiences in the main list; the
  // "Used by a loop" filter narrows to them. Not creatable from the audience form.
  AUDIENCE_CREATION_METHOD.LOOP,
];

const AUDIENCE_CREATION_METHOD_FILTER_ALLOWLIST = new Set([
  ...DEFAULT_CREATION_METHOD_FILTER,
  ...AUDIENCES_CREATION_METHOD_OPTIONS.map((option) => option.value),
]);

/**
 * Remove stale internal creation methods restored from saved URLs/localStorage.
 * An empty/invalid saved selection falls back to the current safe default.
 */
export const sanitizeCreationMethodFilter = (values: readonly string[]): string[] => {
  const sanitized = [...new Set(
    values.filter((value) => AUDIENCE_CREATION_METHOD_FILTER_ALLOWLIST.has(value)),
  )];

  return sanitized.length > 0 ? sanitized : [...DEFAULT_CREATION_METHOD_FILTER];
};

export enum AUDIENCE_SCHEDULE_FREQUENCY {
  DAY = 'DAILY',
  WEEK = 'WEEKLY',
  MONTH = 'MONTHLY',
  YEAR = 'YEARLY',
  HOUR = 'HOURLY',
}

export const PREDEFINED_FREQUENCY_OPTIONS = [
  { label: 'Hourly', value: 'hourly' },
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Custom', value: 'custom' },
];

export const CUSTOM_FREQUENCY_UNIT_OPTIONS = [
  { label: 'Minutes', value: 'minutes' },
  { label: 'Hours', value: 'hours' },
  { label: 'Days', value: 'days' },
];

export const audienceDefaultValue: IAudienceForm = {
  name: '',
  permissions: AUDIENCE_PERMISSION.ANYONE,
  chat_token: '',
  conditionGroups: [
    {
      logicalOperator: 'and',
      groupOperator: 'and',
      conditions: [
        {
          id: uuidv4(),
        },
      ],
    },
  ],
  type: '',
  hubspot_field: '',
  hubspot_value: '',
  cached_contacts: [],
  cached_mappings: {},
  total_count: 0,
  creation_method: AUDIENCE_CREATION_METHOD.RULES,
  batch_size: 50,
  batch_frequency: 'daily',
  schedule_config: {
    frequency: AUDIENCE_SCHEDULE_FREQUENCY.MONTH,
    days: [],
    dayOfWeek: [],
    times: [],
    minutes: [],
    months: [],
  },
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

export const AUDIENCE_SCHEDULE_FREQUENCY_OPTIONS = [
  {
    label: 'Year',
    value: AUDIENCE_SCHEDULE_FREQUENCY.YEAR,
  },
  {
    label: 'Month',
    value: AUDIENCE_SCHEDULE_FREQUENCY.MONTH,
  },
  {
    label: 'Week',
    value: AUDIENCE_SCHEDULE_FREQUENCY.WEEK,
  },
  {
    label: 'Day',
    value: AUDIENCE_SCHEDULE_FREQUENCY.DAY,
  },
  {
    label: 'Hour',
    value: AUDIENCE_SCHEDULE_FREQUENCY.HOUR,
  },
];

export const AUDIENCE_SCHEDULE_DAYS_OPTIONS = [
  ...Array.from({ length: 30 }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
  })),
];

export const AUDIENCE_SCHEDULE_MONTHS_OPTIONS = [
  ...Array.from({ length: 12 }, (_, i) => {
    const monthLabel = new Date(0, i).toLocaleString('default', { month: 'long' });
    const monthShortLabel = new Date(0, i).toLocaleString('default', { month: 'short' });

    return {
      label: monthLabel,
      value: i + 1,
      selectedLabel: monthShortLabel.toUpperCase(),
    };
  }),
];

export const AUDIENCE_SCHEDULE_DAYS_OF_WEEK_OPTIONS = [
  {
    label: 'Sunday',
    value: 0,
    selectedLabel: 'SUN',
  },
  {
    label: 'Monday',
    value: 1,
    selectedLabel: 'MON',
  },
  {
    label: 'Tuesday',
    value: 2,
    selectedLabel: 'TUE',
  },
  {
    label: 'Wednesday',
    value: 3,
    selectedLabel: 'WED',
  },
  {
    label: 'Thursday',
    value: 4,
    selectedLabel: 'THU',
  },
  {
    label: 'Friday',
    value: 5,
    selectedLabel: 'FRI',
  },
  {
    label: 'Saturday',
    value: 6,
    selectedLabel: 'SAT',
  },
];

export const AUDIENCE_SCHEDULE_TIME = [
  {
    label: '12AM',
    value: '00:00',
    calculatedValue: 0,
  },
  {
    label: '1AM',
    value: '01:00',
    calculatedValue: 1,
  },
  {
    label: '2AM',
    value: '02:00',
    calculatedValue: 2,
  },
  {
    label: '3AM',
    value: '03:00',
    calculatedValue: 3,
  },
  {
    label: '4AM',
    value: '04:00',
    calculatedValue: 4,
  },
  {
    label: '5AM',
    value: '05:00',
    calculatedValue: 5,
  },
  {
    label: '6AM',
    value: '06:00',
    calculatedValue: 6,
  },
  {
    label: '7AM',
    value: '07:00',
    calculatedValue: 7,
  },
  {
    label: '8AM',
    value: '08:00',
    calculatedValue: 8,
  },
  {
    label: '9AM',
    value: '09:00',
    calculatedValue: 9,
  },
  {
    label: '10AM',
    value: '10:00',
    calculatedValue: 10,
  },
  {
    label: '11AM',
    value: '11:00',
    calculatedValue: 11,
  },
  {
    label: '12PM',
    value: '12:00',
    calculatedValue: 12,
  },
  {
    label: '1PM',
    value: '13:00',
    calculatedValue: 13,
  },
  {
    label: '2PM',
    value: '14:00',
    calculatedValue: 14,
  },
  {
    label: '3PM',
    value: '15:00',
    calculatedValue: 15,
  },
  {
    label: '4PM',
    value: '16:00',
    calculatedValue: 16,
  },
  {
    label: '5PM',
    value: '17:00',
    calculatedValue: 17,
  },
  {
    label: '6PM',
    value: '18:00',
    calculatedValue: 18,
  },
  {
    label: '7PM',
    value: '19:00',
    calculatedValue: 19,
  },
  {
    label: '8PM',
    value: '20:00',
    calculatedValue: 20,
  },
  {
    label: '9PM',
    value: '21:00',
    calculatedValue: 21,
  },
  {
    label: '10PM',
    value: '22:00',
    calculatedValue: 22,
  },
  {
    label: '11PM',
    value: '23:00',
    calculatedValue: 23,
  },
];

export const AUDIENCE_SCHEDULE_MINUTES = [
  ...Array.from({ length: 13 }, (_, i) => ({
    label: i === 12 ? '59' : `0${i * 5}`.slice(-2),
    value: i === 12 ? 59 : i * 5,
  })),
];
